import React, { useEffect, useMemo, useState } from "react";
import "./admin-news.css";

const emptyPhoto = () => ({ src: "", alt: "", caption: "", sourceWidth: 1600, sourceHeight: 900 });

const emptyArticle = () => ({
  slug: "",
  type: "News",
  date: "",
  dateTime: new Date().toISOString().slice(0, 10),
  location: "Singapore",
  title: "",
  summary: "",
  introduction: "",
  themes: [],
  related: [],
  cover: emptyPhoto(),
  sections: [{ heading: "", body: "", photo: emptyPhoto() }],
  sourceNote: "",
});

const toDraft = (item) => ({
  ...emptyArticle(),
  ...item,
  themes: item.themes || [],
  related: item.related || [],
  cover: { ...emptyPhoto(), ...(item.cover || {}) },
  sections: (item.sections || []).map((section) => ({
    ...section,
    body: Array.isArray(section.paragraphs) ? section.paragraphs.join("\n\n") : section.body || "",
    photo: { ...emptyPhoto(), ...(section.photo || {}) },
  })),
});

const api = async (path, options = {}) => {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || "Request failed");
  return body;
};

const readFile = (file) => new Promise((resolveFile, rejectFile) => {
  const reader = new FileReader();
  reader.onload = () => resolveFile(reader.result);
  reader.onerror = () => rejectFile(new Error("Unable to read image"));
  reader.readAsDataURL(file);
});

const slugFromTitle = (title) => title
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")
  .slice(0, 90);

const PhotoFields = ({ photo, label, onChange, onUpload, uploading }) => (
  <fieldset className="admin-photo-fields">
    <legend>{label}</legend>
    <div className="admin-field admin-field-wide">
      <label>Image URL</label>
      <input value={photo.src || ""} onChange={(event) => onChange({ ...photo, src: event.target.value })} placeholder="https://... or /api/media/..." />
    </div>
    <div className="admin-upload-row">
      <label className="admin-upload-control">
        <span>{uploading ? "Uploading..." : "Upload image"}</span>
        <input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={(event) => onUpload(event.target.files?.[0])} />
      </label>
      <small>JPG, PNG or WebP, up to 5 MB.</small>
    </div>
    <div className="admin-field-grid">
      <div className="admin-field">
        <label>Alt text</label>
        <input value={photo.alt || ""} onChange={(event) => onChange({ ...photo, alt: event.target.value })} placeholder="Describe the image" />
      </div>
      <div className="admin-field">
        <label>Caption</label>
        <input value={photo.caption || ""} onChange={(event) => onChange({ ...photo, caption: event.target.value })} placeholder="Optional caption" />
      </div>
    </div>
    {photo.src && <img className="admin-image-preview" src={photo.src} alt="Selected media preview" />}
  </fieldset>
);

const ArticleForm = ({ article, saving, onSave, onDelete, onCancel }) => {
  const [draft, setDraft] = useState(() => toDraft(article || emptyArticle()));
  const [uploading, setUploading] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(toDraft(article || emptyArticle()));
    setError("");
  }, [article]);

  const update = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const updateSection = (index, value) => setDraft((current) => ({
    ...current,
    sections: current.sections.map((section, itemIndex) => itemIndex === index ? { ...section, ...value } : section),
  }));

  const upload = async (file, target) => {
    if (!file) return;
    setError("");
    setUploading(target);
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error("Images must be smaller than 5 MB");
      const data = await readFile(file);
      const result = await api("/api/admin/uploads", {
        method: "POST",
        body: JSON.stringify({ data, type: file.type }),
      });
      if (target === "cover") {
        update("cover", { ...draft.cover, src: result.src, alt: draft.cover.alt || file.name });
      } else {
        const index = Number(target.replace("section-", ""));
        updateSection(index, { photo: { ...draft.sections[index].photo, src: result.src, alt: draft.sections[index].photo.alt || file.name } });
      }
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setUploading("");
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await onSave({
        ...draft,
        themes: Array.isArray(draft.themes) ? draft.themes : draft.themes.split(","),
        related: Array.isArray(draft.related) ? draft.related : draft.related.split(","),
      });
    } catch (saveError) {
      setError(saveError.message);
    }
  };

  const isNew = !article?.slug;

  return (
    <form className="admin-editor" onSubmit={submit}>
      <div className="admin-editor-heading">
        <div>
          <span className="admin-kicker">{isNew ? "NEW ARTICLE" : "EDIT ARTICLE"}</span>
          <h2>{isNew ? "Create a news article" : article.title}</h2>
        </div>
        <div className="admin-editor-actions">
          <button type="button" className="admin-button admin-button-quiet" onClick={onCancel}>Cancel</button>
          <button type="submit" className="admin-button admin-button-primary" disabled={saving || Boolean(uploading)}>{saving ? "Saving..." : "Save article"}</button>
        </div>
      </div>

      {error && <p className="admin-message admin-message-error" role="alert">{error}</p>}

      <div className="admin-form-section">
        <h3>Publishing details</h3>
        <div className="admin-field-grid">
          <div className="admin-field admin-field-wide">
            <label>Title *</label>
            <input required value={draft.title} onChange={(event) => {
              const title = event.target.value;
              update("title", title);
              if (!draft.slug) update("slug", slugFromTitle(title));
            }} />
          </div>
          <div className="admin-field">
            <label>Article type</label>
            <select value={draft.type} onChange={(event) => update("type", event.target.value)}>
              <option>News</option><option>Events</option><option>Insights</option><option>Company update</option>
            </select>
          </div>
          <div className="admin-field">
            <label>Publish date *</label>
            <input required type="date" value={draft.dateTime} onChange={(event) => {
              update("dateTime", event.target.value);
              if (!draft.date || draft.date === article?.date) update("date", event.target.value);
            }} />
          </div>
          <div className="admin-field">
            <label>Display date</label>
            <input value={draft.date} onChange={(event) => update("date", event.target.value)} placeholder="21 August 2026" />
          </div>
          <div className="admin-field">
            <label>Location</label>
            <input value={draft.location} onChange={(event) => update("location", event.target.value)} />
          </div>
          <div className="admin-field admin-field-wide">
            <label>Article URL</label>
            <input value={draft.slug} onChange={(event) => update("slug", slugFromTitle(event.target.value))} placeholder="article-url-slug" />
            <small>Used after <code>#/insights/</code>. Use English letters, numbers and hyphens.</small>
          </div>
        </div>
        <div className="admin-field admin-field-wide">
          <label>Summary *</label>
          <textarea required rows="3" value={draft.summary} onChange={(event) => update("summary", event.target.value)} />
        </div>
        <div className="admin-field admin-field-wide">
          <label>Opening paragraph</label>
          <textarea rows="4" value={draft.introduction} onChange={(event) => update("introduction", event.target.value)} />
        </div>
        <div className="admin-field-grid">
          <div className="admin-field">
            <label>Topics</label>
            <input value={Array.isArray(draft.themes) ? draft.themes.join(", ") : draft.themes} onChange={(event) => update("themes", event.target.value)} placeholder="Enterprise AI, Singapore" />
          </div>
          <div className="admin-field">
            <label>Related products</label>
            <input value={Array.isArray(draft.related) ? draft.related.join(", ") : draft.related} onChange={(event) => update("related", event.target.value)} placeholder="AI Agents, LLM Gateway" />
          </div>
        </div>
      </div>

      <div className="admin-form-section">
        <h3>Cover image</h3>
        <PhotoFields photo={draft.cover} label="Cover" uploading={uploading === "cover"} onChange={(cover) => update("cover", cover)} onUpload={(file) => upload(file, "cover")} />
      </div>

      <div className="admin-form-section">
        <div className="admin-section-heading-row">
          <h3>Article body</h3>
          <button type="button" className="admin-button admin-button-quiet" onClick={() => update("sections", [...draft.sections, { heading: "", body: "", photo: emptyPhoto() }])}>Add section</button>
        </div>
        {draft.sections.map((section, index) => (
          <section className="admin-story-section" key={`section-${index}`}>
            <div className="admin-section-heading-row">
              <strong>Section {index + 1}</strong>
              {draft.sections.length > 1 && <button type="button" className="admin-text-button admin-danger" onClick={() => update("sections", draft.sections.filter((_, itemIndex) => itemIndex !== index))}>Remove</button>}
            </div>
            <div className="admin-field">
              <label>Heading *</label>
              <input required value={section.heading} onChange={(event) => updateSection(index, { heading: event.target.value })} />
            </div>
            <div className="admin-field">
              <label>Body *</label>
              <textarea required rows="8" value={section.body} onChange={(event) => updateSection(index, { body: event.target.value })} placeholder="Use an empty line to start a new paragraph." />
              <small>Use an empty line to start a new paragraph.</small>
            </div>
            <PhotoFields photo={section.photo} label="Optional image in this section" uploading={uploading === `section-${index}`} onChange={(photo) => updateSection(index, { photo })} onUpload={(file) => upload(file, `section-${index}`)} />
          </section>
        ))}
      </div>

      <div className="admin-form-section">
        <div className="admin-field admin-field-wide">
          <label>Source note</label>
          <input value={draft.sourceNote} onChange={(event) => update("sourceNote", event.target.value)} placeholder="Photo credit or source note" />
        </div>
      </div>

      <div className="admin-editor-footer">
        {!isNew && <button type="button" className="admin-text-button admin-danger" onClick={() => onDelete(article)}>Delete this article</button>}
        <div className="admin-editor-actions">
          <button type="button" className="admin-button admin-button-quiet" onClick={onCancel}>Cancel</button>
          <button type="submit" className="admin-button admin-button-primary" disabled={saving || Boolean(uploading)}>{saving ? "Saving..." : "Save article"}</button>
        </div>
      </div>
    </form>
  );
};

export const AdminNewsPage = ({ items = [], onRefresh, onExit }) => {
  const [session, setSession] = useState({ checking: true, authenticated: false, configured: true });
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [creating, setCreating] = useState(false);
  const editingArticle = useMemo(() => items.find((item) => item.slug === editingSlug), [items, editingSlug]);

  const refreshSession = async () => {
    try {
      const result = await api("/api/admin/session", { method: "GET" });
      setSession({ checking: false, ...result });
    } catch (error) {
      setSession({ checking: false, authenticated: false, configured: false });
      setMessage(error.message);
    }
  };

  useEffect(() => { refreshSession(); }, []);

  const signIn = async (event) => {
    event.preventDefault();
    setBusy(true); setMessage("");
    try {
      await api("/api/admin/login", { method: "POST", body: JSON.stringify({ password }) });
      setPassword("");
      await refreshSession();
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };

  const saveArticle = async (article) => {
    setBusy(true); setMessage("");
    try {
      const result = await api(article.slug && editingArticle ? `/api/admin/insights/${editingArticle.slug}` : "/api/admin/insights", {
        method: article.slug && editingArticle ? "PUT" : "POST",
        body: JSON.stringify(article),
      });
      await onRefresh();
      setEditingSlug(result.item.slug);
      setCreating(false);
      setMessage("Article saved. It is now visible on News & Insights.");
    } finally { setBusy(false); }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Delete “${article.title}”? This cannot be undone.`)) return;
    setBusy(true); setMessage("");
    try {
      await api(`/api/admin/insights/${article.slug}`, { method: "DELETE" });
      await onRefresh();
      setEditingSlug(null);
      setCreating(false);
      setMessage("Article deleted.");
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };

  const signOut = async () => {
    await api("/api/admin/logout", { method: "POST" });
    setSession({ checking: false, authenticated: false, configured: true });
    setEditingSlug(null); setCreating(false); setMessage("");
  };

  if (session.checking) return <main className="admin-page"><p className="admin-loading">Loading news management...</p></main>;

  if (!session.authenticated) {
    return <main className="admin-page"><section className="admin-login-panel">
      <a className="admin-brand" href="#/insights" onClick={(event) => { event.preventDefault(); onExit(); }}>GOLDLY AI</a>
      <span className="admin-kicker">PRIVATE AREA</span>
      <h1>News management</h1>
      <p>Sign in to create, edit and publish items in News &amp; Insights.</p>
      {!session.configured ? <p className="admin-message admin-message-error">Admin access has not been configured on this server.</p> : <form onSubmit={signIn}>
        <label className="admin-field"><span>Password</span><input autoFocus type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        {message && <p className="admin-message admin-message-error" role="alert">{message}</p>}
        <button className="admin-button admin-button-primary admin-login-button" disabled={busy}>{busy ? "Signing in..." : "Sign in"}</button>
      </form>}
      <button className="admin-text-button" onClick={onExit}>Return to News &amp; Insights</button>
    </section></main>;
  }

  return <main className="admin-page"><div className="admin-shell">
    <header className="admin-topbar">
      <div><a className="admin-brand" href="#/insights" onClick={(event) => { event.preventDefault(); onExit(); }}>GOLDLY AI</a><span>News management</span></div>
      <div className="admin-topbar-actions"><button className="admin-button admin-button-quiet" onClick={onExit}>View public news</button><button className="admin-button admin-button-quiet" onClick={signOut}>Sign out</button></div>
    </header>
    <div className="admin-workspace">
      <aside className="admin-article-list" aria-label="Articles">
        <div className="admin-list-heading"><div><span className="admin-kicker">ARTICLES</span><h1>News &amp; Insights</h1></div><button className="admin-button admin-button-primary" onClick={() => { setCreating(true); setEditingSlug(null); setMessage(""); }}>New article</button></div>
        {message && <p className="admin-message admin-message-success" role="status">{message}</p>}
        <div className="admin-list-items">
          {items.map((item) => <button key={item.slug} className={`admin-list-item${editingSlug === item.slug && !creating ? " is-active" : ""}`} onClick={() => { setEditingSlug(item.slug); setCreating(false); setMessage(""); }}>
            <span>{item.type}</span><strong>{item.title}</strong><small>{item.date}</small>
          </button>)}
        </div>
      </aside>
      <section className="admin-editor-pane">
        {creating || editingArticle ? <ArticleForm article={creating ? null : editingArticle} saving={busy} onSave={saveArticle} onDelete={deleteArticle} onCancel={() => { setCreating(false); setEditingSlug(null); }} /> : <div className="admin-empty-state"><span className="admin-kicker">SELECT AN ARTICLE</span><h2>Choose an item to edit, or create a new one.</h2><button className="admin-button admin-button-primary" onClick={() => setCreating(true)}>New article</button></div>}
      </section>
    </div>
  </div></main>;
};
